import type { QueryClient, QueryKey } from '@tanstack/react-query'

export const DASHBOARD_WIDGET_TIMINGS_STORAGE_KEY = 'otw-dashboard-widget-timings'
export const OVERVIEW_NAV_MARK = 'otw-overview-nav'

const SAFE_QUERY_ROOTS = new Set([
  'dashboard',
  'dashboard-widget',
  'dashboard-rollup',
  'expenses',
  'master_inventory',
  'contacts',
  'users',
  'checkbook',
])

export type DashboardLoadKind = 'cold' | 'refresh'

export type DashboardWidgetTiming = {
  id: string
  ttfdMs: number
  widgetMountMs?: number
  queryMs?: number
  queryKeys?: string[]
  notes?: string
  fromCache?: boolean
}

export type DashboardWidgetTimingsSnapshot = {
  timestamp: string
  kind: DashboardLoadKind
  restoredQueryCache: boolean
  navigationType?: string
  widgets: DashboardWidgetTiming[]
  queries: Array<{ queryKey: string; queryMs: number; fromCache?: boolean }>
}

type TimingWindow = Window & {
  __OTW_DASHBOARD_WIDGET_TIMINGS__?: DashboardWidgetTimingsSnapshot
  __OTW_DASHBOARD_CACHE_RESTORED__?: boolean
}

const queryDurations = new Map<string, { queryMs: number; fromCache?: boolean }>()
const recordedWidgetIds = new Set<string>()
let sessionStartedAt = 0
let queryUnsubscribe: (() => void) | undefined
let fetchStartedAt = new Map<string, { started: number; hadData: boolean }>()

const looksLikeEmail = (value: string) => value.includes('@')

export const dashboardWidgetTimingsEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  const envFlag = process.env.VITE_FF_DASHBOARD_WIDGET_TIMINGS
  if (envFlag === 'false') return false
  if (envFlag === 'true') return true
  if (process.env.NODE_ENV === 'development') return true
  const host = window.location.hostname
  if (host.includes('--pr-') && host.endsWith('.web.app')) return true
  return process.env.VITE_FIREBASE_PROJECT_ID === 'otw-stg'
}

export const sanitizeQueryKey = (queryKey: QueryKey): string | undefined => {
  const parts = Array.from(queryKey)
  const root = String(parts[0] ?? '')
  if (!SAFE_QUERY_ROOTS.has(root)) return undefined
  return parts
    .slice(0, 4)
    .map(part => {
      if (part == null) return 'null'
      if (typeof part === 'number' || typeof part === 'boolean') return String(part)
      if (part instanceof Date) return part.toISOString()
      if (typeof part === 'string') {
        if (looksLikeEmail(part)) return '[redacted]'
        return part.length > 64 ? `${part.slice(0, 24)}…` : part
      }
      return '[obj]'
    })
    .join('|')
}

const loadKind = (restoredQueryCache: boolean): DashboardLoadKind => {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (restoredQueryCache || nav?.type === 'reload') return 'refresh'
  return 'cold'
}

const snapshot = (): DashboardWidgetTimingsSnapshot => {
  const win = window as TimingWindow
  const existing = win.__OTW_DASHBOARD_WIDGET_TIMINGS__
  const restoredQueryCache = Boolean(win.__OTW_DASHBOARD_CACHE_RESTORED__)
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  return {
    timestamp: existing?.timestamp ?? new Date().toISOString(),
    kind: existing?.kind ?? loadKind(restoredQueryCache),
    restoredQueryCache,
    navigationType: nav?.type,
    widgets: existing?.widgets ? [...existing.widgets] : [],
    queries: [...queryDurations.entries()].map(([queryKey, value]) => ({
      queryKey,
      queryMs: value.queryMs,
      fromCache: value.fromCache,
    })),
  }
}

const persistSnapshot = (next: DashboardWidgetTimingsSnapshot) => {
  const win = window as TimingWindow
  win.__OTW_DASHBOARD_WIDGET_TIMINGS__ = next
  try {
    sessionStorage.setItem(DASHBOARD_WIDGET_TIMINGS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* private mode / quota */
  }
  win.dispatchEvent(new CustomEvent('otw-dashboard-widget-timing', { detail: next }))
}

export const getDashboardWidgetTimings = (): DashboardWidgetTimingsSnapshot | undefined => {
  if (typeof window === 'undefined') return undefined
  return (window as TimingWindow).__OTW_DASHBOARD_WIDGET_TIMINGS__
}

export const startOverviewTimingSession = (queryClient?: QueryClient) => {
  if (!dashboardWidgetTimingsEnabled()) return

  sessionStartedAt = performance.now()
  recordedWidgetIds.clear()
  queryDurations.clear()
  fetchStartedAt = new Map()
  queryUnsubscribe?.()
  queryUnsubscribe = undefined

  try {
    performance.clearMarks(OVERVIEW_NAV_MARK)
    performance.mark(OVERVIEW_NAV_MARK)
  } catch {
    /* Performance API unavailable */
  }

  const restoredQueryCache = Boolean((window as TimingWindow).__OTW_DASHBOARD_CACHE_RESTORED__)
  persistSnapshot({
    timestamp: new Date().toISOString(),
    kind: loadKind(restoredQueryCache),
    restoredQueryCache,
    navigationType: (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
      ?.type,
    widgets: [],
    queries: [],
  })

  attachQueryClientTimings(queryClient)
}

export const stopOverviewTimingSession = () => {
  queryUnsubscribe?.()
  queryUnsubscribe = undefined
}

const attachQueryClientTimings = (queryClient?: QueryClient) => {
  if (!queryClient) return

  queryUnsubscribe = queryClient.getQueryCache().subscribe(event => {
    const query = event?.query
    if (!query) return
    const queryKey = sanitizeQueryKey(query.queryKey)
    if (!queryKey) return

    if (query.state.fetchStatus === 'fetching' && !fetchStartedAt.has(queryKey)) {
      fetchStartedAt.set(queryKey, {
        started: performance.now(),
        hadData: query.state.data !== undefined,
      })
    }

    if (query.state.fetchStatus === 'idle' && fetchStartedAt.has(queryKey)) {
      const startedMeta = fetchStartedAt.get(queryKey)
      fetchStartedAt.delete(queryKey)
      const queryMs = Math.max(0, Math.round(performance.now() - (startedMeta?.started ?? sessionStartedAt)))
      queryDurations.set(queryKey, {
        queryMs,
        fromCache: Boolean(startedMeta?.hadData),
      })
      persistSnapshot(snapshot())
    }
  })
}

export const recordWidgetDataReady = (
  id: string,
  options?: {
    widgetMountMs?: number
    queryKeys?: string[]
    notes?: string
    fromCache?: boolean
  },
) => {
  if (!dashboardWidgetTimingsEnabled() || !id || recordedWidgetIds.has(id)) return
  recordedWidgetIds.add(id)

  const ttfdMs = Math.max(0, Math.round(performance.now() - sessionStartedAt))
  try {
    const measureName = `otw-widget:${id}`
    performance.measure(measureName, {
      start: OVERVIEW_NAV_MARK,
      duration: ttfdMs,
    })
  } catch {
    try {
      performance.mark(`otw-widget-ready:${id}`)
    } catch {
      /* ignore */
    }
  }

  const queryMs = options?.queryKeys
    ?.map(key => queryDurations.get(key)?.queryMs)
    .find(value => typeof value === 'number')

  const next = snapshot()
  next.widgets.push({
    id,
    ttfdMs,
    widgetMountMs: options?.widgetMountMs !== undefined ? Math.round(options.widgetMountMs) : undefined,
    queryMs,
    queryKeys: options?.queryKeys,
    notes: options?.notes,
    fromCache: options?.fromCache,
  })
  persistSnapshot(next)
}
